import { useState } from 'react'
import {
  Button,
  Card,
  CollapsibleCard,
  IconButton,
  Input,
  SectionLabel,
  Textarea,
} from '../../../design/components'
import type { BioSection } from '../types'
import type { CharacterEditing } from '../useCharacterEditing'
import { SHEET_SECTION_ID } from './sheetSections'
import styles from './sections.module.css'

/**
 * Bio prose, collapsed at the foot of the sheet.
 *
 * The identity half of the bio — the portrait, and the full name — lives in the
 * sheet header instead, so what is left here is the reading matter.
 *
 * Fields arrive already visibility-filtered — a non-owner receives nulls where
 * the owner hid something, and there is no way (and no need) to distinguish that
 * from a field never filled in. Empty fields are simply not rendered.
 */
export function BioCard({
  bio,
  editing,
}: {
  bio: BioSection | undefined
  editing?: CharacterEditing | undefined
}) {
  const [open, setOpen] = useState(false)

  if (bio === undefined) return null

  const canEdit = editing?.canEdit === true

  if (canEdit && editing !== undefined && open) {
    return <BioForm bio={bio} editing={editing} onClose={() => setOpen(false)} />
  }

  const fields: Array<[string, string | null]> = [
    ['Nickname', bio.nickname],
    ['Age', bio.age === null ? null : String(bio.age)],
    ['Description', bio.characterDescription],
    ['Backstory', bio.backstory],
    ['First encounter', bio.firstEncounter],
  ]

  const present = fields.filter(([, value]) => value !== null && value.trim() !== '')

  if (present.length === 0 && !canEdit) return null

  return (
    <CollapsibleCard
      id={SHEET_SECTION_ID.bio}
      empty={present.length === 0}
      variant="bio"
      label="Bio"
      action={
        canEdit ? (
          <IconButton label="Edit bio" icon="edit" onClick={() => setOpen(true)} />
        ) : undefined
      }
    >
      {present.length === 0 && <p className={styles.empty}>Nothing recorded yet.</p>}

      {present.map(([label, value]) => (
        <div key={label} className={styles.bioField}>
          <SectionLabel tone="muted" micro>
            {label}
          </SectionLabel>
          <p className={styles.bioText}>{value}</p>
        </div>
      ))}
    </CollapsibleCard>
  )
}

function BioForm({
  bio,
  editing,
  onClose,
}: {
  bio: BioSection
  editing: CharacterEditing
  onClose: () => void
}) {
  const [fullName, setFullName] = useState(bio.fullName ?? '')
  const [nickname, setNickname] = useState(bio.nickname ?? '')
  const [age, setAge] = useState(bio.age === null ? '' : String(bio.age))
  const [description, setDescription] = useState(bio.characterDescription ?? '')
  const [backstory, setBackstory] = useState(bio.backstory ?? '')
  const [firstEncounter, setFirstEncounter] = useState(bio.firstEncounter ?? '')

  const pending = editing.bio.isPending

  return (
    <Card id={SHEET_SECTION_ID.bio} variant="bio" label="Edit bio">
      <form
        className={styles.inlineForm}
        onSubmit={(event) => {
          event.preventDefault()
          const parsedAge = age.trim() === '' ? null : Number(age)
          // The endpoint replaces the whole bio, so every field goes every time.
          editing.bio.mutate(
            {
              fullName: blankToNull(fullName),
              nickname: blankToNull(nickname),
              age: parsedAge !== null && Number.isFinite(parsedAge) ? parsedAge : null,
              backstory: blankToNull(backstory),
              firstEncounter: blankToNull(firstEncounter),
              characterDescription: blankToNull(description),
            },
            { onSuccess: onClose },
          )
        }}
      >
        <Input label="Full name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        <Input label="Nickname" value={nickname} onChange={(e) => setNickname(e.target.value)} />
        <Input
          label="Age"
          type="number"
          inputMode="numeric"
          value={age}
          onChange={(e) => setAge(e.target.value)}
        />
        <Textarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Textarea
          label="Backstory"
          value={backstory}
          onChange={(e) => setBackstory(e.target.value)}
        />
        <Textarea
          label="First encounter"
          value={firstEncounter}
          onChange={(e) => setFirstEncounter(e.target.value)}
        />

        <div className={styles.actions}>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" disabled={pending}>
            {pending ? 'Saving…' : 'Save bio'}
          </Button>
        </div>
      </form>
    </Card>
  )
}

const blankToNull = (value: string): string | null => (value.trim() === '' ? null : value)
